import { IntranetSection } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useIntranetContents } from '@/products/intranet/features/intranet/use-intranet';
import {
  useIntranetKnowledgeQuickAccess,
  useReplaceIntranetKnowledgeQuickAccess,
} from '@/products/intranet/features/intranet/use-intranet-knowledge';
import { INTRANET_LIBRARY_PATH } from '@/products/intranet/intranet-paths';

export function KnowledgeQuickAccessPage() {
  usePageTitle('Schnellzugriff');
  const stored = useIntranetKnowledgeQuickAccess();
  const contents = useIntranetContents({
    section: IntranetSection.LIBRARY,
    drafts: true,
    page: 1,
    pageSize: 50,
  });
  const replace = useReplaceIntranetKnowledgeQuickAccess();
  const [order, setOrder] = useState<string[] | null>(null);
  const [addId, setAddId] = useState('');
  const items = stored.data?.items ?? [];
  const ids = order ?? items.map((item) => item.contentId);
  const titles = new Map<string, { title: string; published: boolean }>();
  for (const item of items) {
    titles.set(item.contentId, { title: item.title, published: item.published });
  }
  for (const item of contents.data?.items ?? []) {
    if (!titles.has(item.id)) {
      titles.set(item.id, {
        title: item.title,
        published: item.state === 'published' || (item.state == null && item.isDraft === false),
      });
    }
  }

  function move(index: number, delta: number) {
    const next = [...ids];
    const target = index + delta;
    const current = next[index];
    const neighbour = next[target];
    if (current === undefined || neighbour === undefined) {
      return;
    }
    next[index] = neighbour;
    next[target] = current;
    setOrder(next);
  }

  async function save() {
    try {
      await replace.mutateAsync({ contentIds: ids });
      setOrder(null);
      toast.success('Schnellzugriff gespeichert.');
    } catch (error) {
      const message =
        error instanceof ApiRequestError ? error.error.message : 'Speichern ist fehlgeschlagen.';
      toast.error(message);
    }
  }

  return (
    <section className="flex flex-col gap-6">
      <p>
        <Link
          to={INTRANET_LIBRARY_PATH}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Zur Wissensdatenbank
        </Link>
      </p>
      <PlatformPageHeader
        title="Schnellzugriff"
        description="Die Reihenfolge gilt für den Mandanten. Eine unveröffentlichte Seite bleibt gespeichert und ist für Leser unsichtbar."
      />
      {stored.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Schnellzugriff wird geladen …
        </p>
      ) : stored.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Der Schnellzugriff konnte nicht geladen werden.
        </p>
      ) : (
        <>
          {ids.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border bg-card px-4 py-8 text-center text-sm">
              Noch keine Seite im Schnellzugriff.
            </p>
          ) : (
            <ol className="flex flex-col gap-2">
              {ids.map((id, index) => {
                const entry = titles.get(id);
                return (
                  <li
                    key={id}
                    className="flex flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 sm:flex-row sm:items-center"
                  >
                    <span className="min-w-0 flex-1 font-semibold text-primary">
                      {entry?.title ?? id}
                      {entry && !entry.published ? (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          Nicht veröffentlicht
                        </span>
                      ) : null}
                    </span>
                    <span className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        Nach oben
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={index === ids.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        Nach unten
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setOrder(ids.filter((entryId) => entryId !== id))}
                      >
                        Entfernen
                      </Button>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Select
              value={addId || 'none'}
              onValueChange={(value) => setAddId(value === 'none' ? '' : value)}
            >
              <SelectTrigger aria-label="Seite hinzufügen" className="w-full sm:max-w-md">
                <SelectValue placeholder="Seite wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seite wählen</SelectItem>
                {(contents.data?.items ?? [])
                  .filter((item) => !ids.includes(item.id))
                  .map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.title}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              disabled={addId === ''}
              onClick={() => {
                setOrder([...ids, addId]);
                setAddId('');
              }}
            >
              Hinzufügen
            </Button>
            <Button type="button" disabled={replace.isPending} onClick={() => void save()}>
              Speichern
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
